import { Navigate, Route, Routes, NavLink, useLocation } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { AppScopeBanner } from '@/components/AppScopeBanner';
import {
  LayoutDashboard,
  BookOpen,
  List,
  Ruler,
  Link2,
  GitBranch,
  Clock,
  AlertTriangle,
} from 'lucide-react';

// Lazy-load all sub-pages (using named-export wrappers where needed)
const ContextualizerDashboard = lazy(() => import('./pages/ContextualizerDashboard'));
const TemplatesPanel = lazy(() =>
  import('./components/TemplatesPanel').then((m) => ({ default: m.TemplatesPanel }))
);
const AttributesPanel = lazy(() =>
  import('./components/AttributesPanel').then((m) => ({ default: m.AttributesPanel }))
);
const UOMsPanel = lazy(() =>
  import('./components/UOMsPanel').then((m) => ({ default: m.UOMsPanel }))
);
const RelationshipTypesPanel = lazy(() =>
  import('./components/RelationshipTypesPanel').then((m) => ({ default: m.RelationshipTypesPanel }))
);
const SubcategoryRulesPanel = lazy(() =>
  import('./components/SubcategoryRulesPanel').then((m) => ({ default: m.SubcategoryRulesPanel }))
);
const PendingApprovalsPanel = lazy(() =>
  import('./components/PendingApprovalsPanel').then((m) => ({ default: m.PendingApprovalsPanel }))
);
const ConflictsPanel = lazy(() => import('./components/ConflictsPanel'));
// TemplateDetailPage exports a default route wrapper
const TemplateDetailRoute = lazy(() => import('./components/TemplateDetailPage'));

const NAV_ITEMS = [
  { label: 'Dashboard', path: '', icon: LayoutDashboard },
  { label: 'Templates', path: 'templates', icon: BookOpen },
  { label: 'Attributes', path: 'attributes', icon: List },
  { label: 'Units', path: 'units', icon: Ruler },
  { label: 'Relationships', path: 'relationships', icon: Link2 },
  { label: 'Rules', path: 'rules', icon: GitBranch },
  { label: 'Approvals', path: 'approvals', icon: Clock },
  { label: 'Conflicts', path: 'conflicts', icon: AlertTriangle },
] as const;

function NavBar() {
  const location = useLocation();
  const base = '/contextualizer';
  const sub = location.pathname.replace(base, '').replace(/^\//, '').split('/')[0] || '';

  return (
    <div className="flex items-center gap-1 px-6 pt-5 pb-2 flex-wrap">
      <div className="flex items-center gap-2 mr-6">
        <div className="w-8 h-8 rounded-lg bg-violet-600 flex items-center justify-center">
          <BookOpen className="w-4 h-4 text-white" />
        </div>
        <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">Contextualizer</span>
      </div>

      <nav className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 flex-wrap">
        {NAV_ITEMS.map((item) => {
          const isActive = sub === item.path || (item.path === '' && sub === '');
          const Icon = item.icon;
          return (
            <NavLink
              key={item.path}
              to={item.path === '' ? base : `${base}/${item.path}`}
              end={item.path === ''}
              className={`
                flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors
                ${isActive
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700'}
              `}
            >
              <Icon className="w-3.5 h-3.5" />
              {item.label}
            </NavLink>
          );
        })}
      </nav>
    </div>
  );
}

function FallbackSpinner() {
  return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" />
    </div>
  );
}

export default function ContextualizerRouter() {
  return (
    <div className="page-contextualizer h-full flex flex-col">
      <NavBar />
      <div className="px-6 pt-2">
        <AppScopeBanner requireApp={false} />
      </div>
      <div className="flex-1 min-h-0 px-6 pb-6 overflow-auto">
        <Suspense fallback={<FallbackSpinner />}>
          <Routes>
            <Route index element={<ContextualizerDashboard />} />
            <Route path="templates" element={<TemplatesPanel />} />
            <Route path="templates/:templateId" element={<TemplateDetailRoute />} />
            <Route path="attributes" element={<AttributesPanel />} />
            <Route path="units" element={<UOMsPanel />} />
            <Route path="relationships" element={<RelationshipTypesPanel />} />
            <Route path="rules" element={<SubcategoryRulesPanel />} />
            <Route path="approvals" element={<PendingApprovalsPanel />} />
            <Route path="conflicts" element={<ConflictsPanel />} />
            <Route path="*" element={<Navigate to="/contextualizer" replace />} />
          </Routes>
        </Suspense>
      </div>
    </div>
  );
}
